import {
  asDraggableData,
  destinationFor,
  droppableId,
  type DroppableData,
} from "@/dnd/ids";
import { cnTransferDropTargetSurface } from "@/dnd/transferDropTargetStyles";
import { useTransferDropHighlight } from "@/dnd/useTransferDropHighlight";
import { useRailOsDropHighlightId } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/RailFilesDropHighlight";
import { RailOsFileDropZone } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/RailOsFileDropZone";
import { useRailScopeOsDrop } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/useRailScopeOsDrop";
import {
  RAIL_LIST_ITEM_ATTR,
  RAIL_SELECTED_ATTR,
  useRailRowSelection,
  type RailSelectionSource,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { WorkspacesRailDraggableCanvasRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailDraggableCanvasRow";
import { useCanvasNodesMirrorSynced } from "@/pages/canvas/context/CanvasNodesMirrorContext";
import type { Canvas } from "@cohesive/api-client";
import { useDndContext, useDroppable } from "@dnd-kit/core";
import { useMemo } from "react";

/**
 * The canvas node as a file drop target (add a file into this canvas — copy into
 * context). Wraps the whole node (header + expanded files), so a file can be
 * dropped anywhere on it. When the canvas is open it uses the `canvas-scope`
 * surface so the dispatcher places a node on the board; otherwise the
 * `rail-canvas-row` surface just copies the file into the canvas's scope.
 * OS file drops upload straight into the canvas's scope.
 *
 * It is a FILE-only zone, so it stays **disabled** while dragging a canvas or
 * chat — a file zone must never intercept a canvas/chat drag meant for the
 * section or a folder (dnd-kit skips disabled droppables in collision).
 *
 * Also the contiguous-selection list-item host: adjacent canvas nodes are
 * drop-zone siblings, not row siblings.
 */
function CanvasRowDropZone({
  canvasId,
  canvasOpen,
  section = "canonical",
  children,
}: {
  canvasId: string;
  canvasOpen: boolean;
  section?: RailSelectionSource;
  children: React.ReactNode;
}) {
  const { active } = useDndContext();
  const activeIsFiles =
    !!active && asDraggableData(active.data.current)?.kind === "files";

  const { selected } = useRailRowSelection(canvasId, section, "canvas", false);

  const surface = canvasOpen ? "canvas-scope" : "rail-canvas-row";
  const dropId = droppableId.railCanvasRow(canvasId);
  const droppableData = useMemo(
    (): DroppableData => ({
      surface,
      destination: destinationFor(surface, { canvasId }),
    }),
    [surface, canvasId],
  );
  const droppable = useDroppable({
    id: dropId,
    data: droppableData,
    disabled: !activeIsFiles,
  });
  const { isOver, disallowed } = useTransferDropHighlight(
    dropId,
    droppableData.destination,
  );

  const handleOsDrop = useRailScopeOsDrop({ canvasId });
  const osActiveDropId = useRailOsDropHighlightId();
  const osOver = osActiveDropId === dropId;

  return (
    <RailOsFileDropZone
      ref={droppable.setNodeRef}
      dropId={dropId}
      onDrop={handleOsDrop}
      className={cnTransferDropTargetSurface({
        isOver: isOver || osOver,
        disallowed,
      })}
      {...{ [RAIL_LIST_ITEM_ATTR]: "" }}
      {...(selected ? { [RAIL_SELECTED_ATTR]: "" } : {})}
    >
      {children}
    </RailOsFileDropZone>
  );
}

/**
 * A canvas inside the workspace containment tree. The rail shows places, not
 * contents: the row opens the canvas (its files live on the board), it never
 * expands into a file tree. The whole node stays a file drop target — see
 * CanvasRowDropZone above.
 */
export function WorkspacesRailCanvasNode({
  canvas,
  dragNamespace,
  section,
  active,
  onRename,
  onShare,
  onDelete,
}: {
  canvas: Canvas;
  dragNamespace: string;
  /** Rail section this node lives in — drives the source-aware highlight. */
  section?: RailSelectionSource;
  active?: boolean;
  onRename: (canvas: Canvas) => void;
  onShare?: (canvas: Canvas) => void;
  onDelete?: (canvas: Canvas) => void;
}) {
  // The rail shows places, never their contents — a canvas is a row you
  // open, not a tree you expand. Its files live on the canvas.
  const canvasOpen = useCanvasNodesMirrorSynced(canvas.id);

  return (
    <CanvasRowDropZone
      canvasId={canvas.id}
      canvasOpen={canvasOpen}
      section={section}
    >
      <div className="flex flex-col">
        <WorkspacesRailDraggableCanvasRow
          canvas={canvas}
          dragNamespace={dragNamespace}
          section={section}
          active={active}
          onRename={onRename}
          onShare={onShare}
          onDelete={onDelete}
        />
      </div>
    </CanvasRowDropZone>
  );
}
